import React, { useEffect, useState, useCallback } from 'react';
import { Shield, Clock, AlertTriangle, RefreshCw, FileText, User, Upload, CheckCircle, Briefcase, Building2 } from 'lucide-react';
import { toast } from 'sonner';
import { apiCall, API_URL } from '../utils/api';
import OrganizationSetup from './employer/OrganizationSetup';
import AgencySetup from './agent/AgencySetup';
import ScensobConnectLogo from './brand/ScensobConnectLogo';
import AddressVerifier, { flattenAddress, parseAddressString } from './AddressVerifier';

/**
 * VerificationGate
 * ----------------
 * Wraps a dashboard page. If the current user has not been admin-verified yet,
 * it renders the verification flow (profile completion, document uploads, status
 * banners) instead of the protected children.
 *
 * Status handling:
 *   approved           → renders children
 *   profile_incomplete → renders multi-section profile form + doc uploads
 *   under_review       → renders an "awaiting verification" banner
 *   rejected           → renders rejection banner with reason + resubmit form
 */
export default function VerificationGate({ user, children }) {
  const [loading, setLoading] = useState(true);
  const [state, setState] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const res = await apiCall('/api/verification/me');
      setState(res);
    } catch (e) {
      // If the call itself was somehow blocked, surface the error but allow
      // children to render so we don't lock the admin out by accident.
      console.error('verification/me failed:', e);
      setState({ verification_status: 'approved' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-blue-50 to-purple-50">
        <div className="flex items-center gap-3 text-slate-600">
          <RefreshCw className="w-5 h-5 animate-spin" />
          <span>Checking verification status...</span>
        </div>
      </div>
    );
  }

  const status = state?.verification_status;

  // Approved users see the actual app.
  if (status === 'approved') return children;

  return (
    <VerificationFlow user={user} state={state} onRefresh={refresh} />
  );
}

// =============================================================
// Verification UI
// =============================================================
function VerificationFlow({ user, state, onRefresh }) {
  const role = state?.role || user?.role;
  const status = state?.verification_status;
  const portalLabel = (
    role === 'worker' ? 'Worker Portal' :
    role === 'agent' ? 'Staffing Agency Portal' :
    role === 'employer' ? 'Employer Portal' : 'Account Setup'
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-purple-50">
      {/* Top portal-branded bar so users immediately see they ARE on the
          correct portal (worker / agent / employer) even before approval. */}
      <div className={`w-full ${role === 'agent'
        ? 'bg-gradient-to-r from-teal-600 via-cyan-600 to-blue-600'
        : role === 'worker'
          ? 'bg-white border-b border-slate-200'
          : 'bg-gradient-to-r from-violet-600 via-purple-600 to-indigo-600'} ${role === 'worker' ? '' : 'text-white'}`}>
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <ScensobConnectLogo size={32} subtitle={portalLabel} wordmarkColor={role === 'worker' ? '#0f172a' : '#fff'} />
          <button
            onClick={() => { localStorage.clear(); window.location.href = '/login'; }}
            className={`text-sm font-medium ${role === 'worker' ? 'text-slate-600 hover:text-slate-900' : 'text-white/90 hover:text-white'}`}
            data-testid="verification-logout-btn-top"
          >
            Log out
          </button>
        </div>
      </div>

      <div className="py-8 px-4">
        <div className="max-w-3xl mx-auto">
          {/* Header */}
          <div className="flex items-center gap-3 mb-6">
            <div className={`p-3 rounded-2xl shadow-lg ${
              role === 'agent' ? 'bg-gradient-to-br from-teal-600 to-cyan-600' :
              role === 'worker' ? 'bg-gradient-to-br from-blue-600 to-indigo-600' :
              'bg-gradient-to-br from-violet-600 to-purple-600'
            }`}>
              <Shield className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Account Verification — {portalLabel}</h1>
              <p className="text-sm text-slate-600">
                Welcome {user?.full_name || user?.email}. Before you can use ScensobConnect's {portalLabel}, an administrator needs to verify your account.
              </p>
            </div>
          </div>

          {/* Status banner */}
          <StatusBanner status={status} reason={state?.rejection_reason} />

          {/* Forms */}
          {(status === 'profile_incomplete' || status === 'rejected') && (
            <VerificationFormRouter role={role} state={state} onSaved={onRefresh} />
          )}

          {/* Footer actions */}
          <div className="mt-6 flex items-center justify-between">
            <button
              onClick={() => { localStorage.clear(); window.location.href = '/login'; }}
              className="text-sm text-slate-600 hover:text-slate-900"
              data-testid="verification-logout-btn"
            >
              Log out
            </button>
            <button
              onClick={onRefresh}
              className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
              data-testid="verification-refresh-btn"
            >
              <RefreshCw className="w-4 h-4" /> Refresh status
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function StatusBanner({ status, reason }) {
  if (status === 'under_review') {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5 mb-6" data-testid="verification-banner-review">
        <div className="flex gap-3">
          <Clock className="w-6 h-6 text-amber-700 flex-shrink-0" />
          <div>
            <h3 className="font-bold text-amber-900">Awaiting admin verification</h3>
            <p className="text-sm text-amber-800 mt-1">
              You've submitted your profile and documents. An administrator is reviewing
              your submission. You'll receive an email when there's a decision —
              usually within 1 business day.
            </p>
          </div>
        </div>
      </div>
    );
  }
  if (status === 'rejected') {
    return (
      <div className="bg-red-50 border border-red-200 rounded-2xl p-5 mb-6" data-testid="verification-banner-rejected">
        <div className="flex gap-3">
          <AlertTriangle className="w-6 h-6 text-red-700 flex-shrink-0" />
          <div className="flex-1">
            <h3 className="font-bold text-red-900">Verification rejected</h3>
            <p className="text-sm text-red-800 mt-1">
              An administrator could not approve your account. Please review the reason
              below, update your profile or documents, and resubmit.
            </p>
            {reason && (
              <div className="mt-3 bg-white border border-red-200 rounded-lg p-3">
                <p className="text-xs font-semibold text-red-700 uppercase tracking-wide">Reason from admin</p>
                <p className="text-sm text-slate-800 mt-1">{reason}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }
  // profile_incomplete or unknown
  return (
    <div className="bg-blue-50 border border-blue-200 rounded-2xl p-5 mb-6" data-testid="verification-banner-incomplete">
      <div className="flex gap-3">
        <FileText className="w-6 h-6 text-blue-700 flex-shrink-0" />
        <div>
          <h3 className="font-bold text-blue-900">Complete your verification profile</h3>
          <p className="text-sm text-blue-800 mt-1">
            Please fill in the fields below and upload the required documents.
            Once everything is complete, submit for admin review.
          </p>
        </div>
      </div>
    </div>
  );
}

function VerificationFormRouter({ role, state, onSaved }) {
  if (role === 'worker') return <WorkerForm state={state} onSaved={onSaved} />;
  if (role === 'employer') {
    // Reuse the existing multi-step Organization Setup wizard inside the gate
    // so employers get the exact same polished UI they already know.
    return (
      <div className="rounded-2xl overflow-hidden shadow-sm border border-slate-200">
        <OrganizationSetup
          embedded
          onClose={null}
          onVerificationComplete={onSaved}
        />
      </div>
    );
  }
  if (role === 'agent') {
    // Staffing agencies have their own dedicated wizard with agency-specific
    // fields (license #, workers managed, insurance docs) — separate flow from
    // employer Organization Setup per product decision.
    return (
      <div className="rounded-2xl overflow-hidden shadow-sm border border-slate-200">
        <AgencySetup
          embedded
          onClose={null}
          onVerificationComplete={onSaved}
        />
      </div>
    );
  }
  return null;
}

// =============================================================
// Worker form
// =============================================================
function WorkerForm({ state, onSaved }) {
  const r = state?.record || {};
  const [form, setForm] = useState({
    full_name: r.full_name || '',
    phone: r.phone || '',
    address: r.address || '',
    date_of_birth: r.date_of_birth || '',
    ssn: '',
    bank_account_holder_name: r.bank_account_holder_name || '',
    // Full account/routing numbers (schema keeps the legacy `_last4`
    // field names for backward-compat, but the values are now the
    // complete digits so payroll can wire the full ACH details).
    bank_account_last4: r.bank_account_last4 || '',
    bank_routing_last4: r.bank_routing_last4 || '',
  });
  // Structured address state — kept in-sync with the single-string
  // `form.address` used by the backend. Seed from any legacy value
  // via best-effort parseAddressString so returning users don't lose
  // what they already typed.
  const [addressStruct, setAddressStruct] = useState(() => parseAddressString(r.address || ''));
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const missing = state?.missing_fields || [];
  const has = (k) => !missing.includes(k);

  const save = async () => {
    setSaving(true);
    try {
      // Flatten the structured address to the single-string schema the
      // backend already expects. Prefer the live struct over the raw
      // string so any autocomplete-driven edit lands in the payload.
      const flat = flattenAddress(addressStruct);
      const payload = { ...form, address: flat || form.address };
      // Strip empty/unchanged SSN so we don't overwrite the hash with blank
      if (!payload.ssn) delete payload.ssn;
      await apiCall('/api/verification/save', { method: 'POST', body: JSON.stringify(payload) });
      toast.success('Profile saved');
      await onSaved();
    } catch (e) {
      toast.error(e?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      await apiCall('/api/verification/submit', { method: 'POST' });
      toast.success('Submitted for admin review');
      await onSaved();
    } catch (e) {
      toast.error(e?.message || 'Submit failed');
    } finally {
      setSubmitting(false);
    }
  };

  const remainingDocs = ['government_id_document','selfie_document','right_to_work_document'].filter(d => missing.includes(d));
  const canSubmit = missing.length === 0;

  return (
    <div className="space-y-5">
      <Section title="Personal information" icon={<User className="w-5 h-5" />}>
        <Field label="Full legal name" required={!has('full_name')}>
          <input data-testid="verif-worker-fullname" value={form.full_name} onChange={(e)=>setForm({...form, full_name:e.target.value})} className={inp} />
        </Field>
        <Field label="Phone" required={!has('phone')}>
          <input data-testid="verif-worker-phone" value={form.phone} onChange={(e)=>setForm({...form, phone:e.target.value})} className={inp} />
        </Field>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            Home address {!has('address') && <span className="text-red-500">*</span>}
          </label>
          <p className="text-xs text-slate-500 mb-2">
            Start typing your street — we'll suggest matches and let you verify the full address.
          </p>
          <AddressVerifier
            colorScheme="blue"
            required={!has('address')}
            value={addressStruct}
            onChange={(next) => {
              setAddressStruct(next);
              // Keep the legacy single-string field in-sync so a plain
              // Save without hitting Verify still ships a full address.
              setForm((prev) => ({ ...prev, address: flattenAddress(next) }));
            }}
            testIdPrefix="verif-worker-address"
          />
        </div>
        <Field label="Date of birth" required={!has('date_of_birth')}>
          <input data-testid="verif-worker-dob" type="date" value={form.date_of_birth} onChange={(e)=>setForm({...form, date_of_birth:e.target.value})} className={inp} />
        </Field>
        <Field label={has('ssn') ? 'SSN (already on file — leave blank to keep)' : 'SSN'} required={!has('ssn')}>
          <input data-testid="verif-worker-ssn" type="password" autoComplete="off" placeholder={has('ssn') ? '••• already provided' : '###-##-####'} value={form.ssn} onChange={(e)=>setForm({...form, ssn:e.target.value})} className={inp} />
          <p className="text-xs text-slate-500 mt-1">Stored securely — we only keep the last 4 digits in plain text.</p>
        </Field>
      </Section>

      <Section title="Banking (for payroll)" icon={<FileText className="w-5 h-5" />}>
        <Field label="Account holder name" required={!has('bank_account_holder_name')}>
          <input data-testid="verif-worker-bank-holder" value={form.bank_account_holder_name} onChange={(e)=>setForm({...form, bank_account_holder_name:e.target.value})} className={inp} />
        </Field>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Account number" required={!has('bank_account_last4')}>
            <input
              data-testid="verif-worker-bank-acct"
              inputMode="numeric"
              autoComplete="off"
              placeholder="e.g. 000123456789"
              value={form.bank_account_last4}
              onChange={(e)=>setForm({...form, bank_account_last4:e.target.value.replace(/\D/g,'').slice(0,17)})}
              className={inp}
            />
            <p className="text-xs text-slate-500 mt-1">Enter your full checking or savings account number (up to 17 digits).</p>
          </Field>
          <Field label="Routing number" required={!has('bank_routing_last4')}>
            <input
              data-testid="verif-worker-bank-routing"
              inputMode="numeric"
              autoComplete="off"
              placeholder="9-digit routing number"
              value={form.bank_routing_last4}
              onChange={(e)=>setForm({...form, bank_routing_last4:e.target.value.replace(/\D/g,'').slice(0,9)})}
              className={inp}
            />
            <p className="text-xs text-slate-500 mt-1">The 9-digit ABA routing number on the bottom-left of your check.</p>
          </Field>
        </div>
      </Section>

      <div className="flex justify-end">
        <button onClick={save} disabled={saving} className={btnSecondary} data-testid="verif-worker-save">
          {saving ? 'Saving…' : 'Save profile'}
        </button>
      </div>

      <Section title="Identity documents" icon={<Upload className="w-5 h-5" />}>
        <DocUpload label="Government-issued ID (driver's license, passport, state ID)" type="government_id_document" uploaded={has('government_id_document')} onSaved={onSaved} testid="verif-worker-doc-id" />
        <DocUpload label="Selfie photo for identity match" type="selfie_document" uploaded={has('selfie_document')} onSaved={onSaved} testid="verif-worker-doc-selfie" />
        <DocUpload label="Right-to-work document (I-9, work permit, etc.)" type="right_to_work_document" uploaded={has('right_to_work_document')} onSaved={onSaved} testid="verif-worker-doc-rtw" />
      </Section>

      <SubmitFooter missing={missing} canSubmit={canSubmit} submitting={submitting} onSubmit={submit} />
    </div>
  );
}

// =============================================================
// Building blocks
// =============================================================
function Section({ title, icon, children }) {
  return (
    <section className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
      <div className="flex items-center gap-2 mb-4">
        <div className="text-blue-600">{icon}</div>
        <h2 className="font-bold text-slate-900">{title}</h2>
      </div>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <label className="block text-sm font-medium text-slate-700 mb-1">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      {children}
    </div>
  );
}

function DocUpload({ label, type, uploaded, onSaved, testid }) {
  const [busy, setBusy] = useState(false);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      // iter77 Phase B: cookie-based auth, no Bearer header needed.
      const fd = new FormData();
      fd.append('document_type', type);
      fd.append('file', file);
      const res = await fetch(`${API_URL}/api/verification/document`, {
        method: 'POST',
        credentials: 'include',
        body: fd,
      });
      if (!res.ok) {
        let msg = `Upload failed (status ${res.status})`;
        try { const d = await res.json(); msg = d?.detail || msg; } catch {/* noop */}
        throw new Error(typeof msg === 'string' ? msg : JSON.stringify(msg));
      }
      toast.success('Document uploaded');
      await onSaved();
    } catch (err) {
      toast.error(err?.message || 'Upload failed');
    } finally {
      setBusy(false);
      e.target.value = '';
    }
  };

  return (
    <div className={`border rounded-xl p-4 flex items-center gap-3 ${uploaded ? 'bg-green-50 border-green-200' : 'bg-slate-50 border-slate-200'}`}>
      <div className={`p-2 rounded-lg flex-shrink-0 ${uploaded ? 'bg-green-100' : 'bg-blue-100'}`}>
        {uploaded ? <CheckCircle className="w-5 h-5 text-green-700" /> : <Upload className="w-5 h-5 text-blue-700" />}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-slate-900">{label}</p>
        <p className="text-xs text-slate-500 mt-0.5">
          {uploaded ? 'Uploaded — you can replace it by choosing a new file below.' : 'JPG, PNG, or PDF (10MB max).'}
        </p>
      </div>
      <label className={`px-3 py-2 text-sm font-medium rounded-lg cursor-pointer ${busy ? 'bg-slate-200 text-slate-500' : 'bg-blue-600 text-white hover:bg-blue-700'}`} data-testid={testid}>
        {busy ? 'Uploading…' : (uploaded ? 'Replace' : 'Upload')}
        <input type="file" className="hidden" accept="image/*,application/pdf" onChange={upload} disabled={busy} />
      </label>
    </div>
  );
}

function SubmitFooter({ missing, canSubmit, submitting, onSubmit }) {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
      <div className="flex items-start gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          {canSubmit ? (
            <p className="text-sm text-green-700 font-medium">✓ All required fields and documents are in. Ready to submit for admin review.</p>
          ) : (
            <div>
              <p className="text-sm text-slate-700 font-medium">Still missing ({missing.length}):</p>
              <ul className="mt-1 text-xs text-slate-600 list-disc ml-5">
                {missing.slice(0, 8).map(m => <li key={m}>{prettyField(m)}</li>)}
                {missing.length > 8 && <li>… and {missing.length - 8} more</li>}
              </ul>
            </div>
          )}
        </div>
        <button
          onClick={onSubmit}
          disabled={!canSubmit || submitting}
          className={`px-5 py-2.5 rounded-xl font-semibold text-white shadow-md transition ${canSubmit ? 'bg-gradient-to-r from-blue-600 to-purple-600 hover:opacity-90' : 'bg-slate-300 cursor-not-allowed'}`}
          data-testid="verif-submit-btn"
        >
          {submitting ? 'Submitting…' : 'Submit for review'}
        </button>
      </div>
    </div>
  );
}

function prettyField(k) {
  const map = {
    full_name: 'Full legal name',
    phone: 'Phone number',
    address: 'Home / business address',
    date_of_birth: 'Date of birth',
    ssn: 'SSN',
    government_id_document: 'Government-issued ID (document)',
    selfie_document: 'Selfie photo (document)',
    right_to_work_document: 'Right-to-work document',
    bank_account_holder_name: 'Bank account holder name',
    bank_account_last4: 'Account number',
    bank_routing_last4: 'Routing number',
    company_name: 'Company name',
    business_type: 'Business type',
    industry: 'Industry',
    tax_id: 'Tax ID / EIN',
    business_registration_number: 'Business registration #',
    description: 'Company description',
    business_license_document: 'Business license (document)',
  };
  return map[k] || k.replace(/_/g, ' ');
}

const inp = 'w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm';
const btnSecondary = 'px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-sm font-medium disabled:opacity-50';
